import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { tx, useLang, txf } from "../../lib/i18n";
import { uploadSheet } from "./sheetUpload";

const B = "/admin/category-bulk";
const ACTIVE = ["validating", "applying", "rolling_back"];
const OUTCOMES = ["will_change", "unchanged", "rejected", "applied", "skipped", "rolled_back", "rollback_skipped"];
const btn = "rounded-lg border border-[var(--mv-border-2)] px-3 py-1.5 text-xs hover:border-[var(--mv-teal)] disabled:opacity-40";

const download = async (path, name) => {
  const { data } = await api.get(path, { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = name; a.click();
};

function JobRows({ job }) {
  const [outcome, setOutcome] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`${B}/jobs/${job.job_id}/rows`, { params: { outcome: outcome || undefined } }).then((r) => setD(r.data)); }, [job.job_id, job.status, outcome]);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">{["", ...OUTCOMES].map((o) => (
        <button key={o || "all"} onClick={() => setOutcome(o)} className={`rounded-full border px-2 py-0.5 text-[11px] ${outcome === o ? "border-[var(--mv-teal)] text-[var(--mv-teal)]" : "border-[var(--mv-border-2)] text-[var(--mv-muted)]"}`} data-testid={`cb-rows-filter-${o || "all"}`}>
          {tx(o || "all")} {o && (job.live_counts?.[o] ?? 0)}</button>))}</div>
      <div className="max-h-[420px] overflow-auto rounded-xl border border-[var(--mv-border)]">
        <table className="w-full text-[11px]" data-testid="cb-rows-table"><thead className="sticky top-0 bg-[var(--mv-card)] text-left text-[var(--mv-dim)]"><tr>
          <th className="p-1.5">{tx("Row")}</th><th className="p-1.5">{tx("Match")}</th><th className="p-1.5">{tx("Product")}</th><th className="p-1.5">{tx("Before")}</th><th className="p-1.5">{tx("After")}</th><th className="p-1.5">{tx("Result")}</th></tr></thead>
          <tbody>{(d?.items || []).map((r) => (
            <tr key={r.row_no} className="border-t border-[var(--mv-border)]" data-testid={`cb-row-${r.row_no}`}>
              <td className="p-1.5">{r.row_no}</td><td className="p-1.5 font-mono">{r.match_type}: {r.match_value}</td>
              <td className="p-1.5">{r.item_number} {r.title}</td>
              <td className="p-1.5 font-mono">{r.before ? `${r.before.category_id || "—"} / ${r.before.subcategory_id || "—"}` : ""}</td>
              <td className="p-1.5 font-mono">{r.after ? `${r.after.category_id} / ${r.after.subcategory_id || "—"}` : ""}</td>
              <td className={`p-1.5 ${r.outcome === "rejected" || r.outcome.endsWith("skipped") ? "text-amber-300" : "text-[var(--mv-teal)]"}`}>{tx(r.outcome)}{r.reason && ` — ${tx(job.reason_text?.[r.reason] || r.reason)}`}</td>
            </tr>))}</tbody></table>
      </div>
      {d && <p className="text-[10px] text-[var(--mv-dim)]">{txf("Showing first {0} of {1}", d.items.length, d.total)}</p>}
    </div>
  );
}

function JobCard({ job, reload }) {
  const c = job.live_counts || {};
  const act = async (path, body) => { try { await api.post(`${B}/jobs/${job.job_id}/${path}`, body); reload(); } catch (e) { toast.error(tx(errMsg(e))); } };
  const apply = () => { const v = window.prompt(tx(txf("This changes ONLY the category and subcategory of {0} products. Type APPLY {0} to confirm.", c.will_change))); if (v) act("apply", { confirm: v }); };
  const rollback = () => { const v = window.prompt(tx("Restore the previous categories for products this job changed? Type ROLLBACK to confirm.")); if (v) act("rollback", { confirm: v }); };
  return (
    <div className="space-y-3 rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4" data-testid={`cb-job-${job.job_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <b>{job.file_name}</b><span className="text-[var(--mv-dim)]">{job.job_id} · {job.total_rows} {tx("rows")}</span>
        <span className="rounded bg-white/5 px-2 py-0.5" data-testid="cb-job-status">{tx(job.status)}</span>
        {job.error && <span className="text-red-300">{job.error}</span>}
        <span className="flex-1" />
        <button className={btn} onClick={() => download(`${B}/jobs/${job.job_id}/preview.csv`, `${job.job_id}_preview.csv`)} data-testid="cb-preview-csv">{tx("Download preview CSV")}</button>
        {job.status === "validated" && c.will_change > 0 && <button className="mv-btn-primary rounded-lg px-3 py-1.5 text-xs" onClick={apply} data-testid="cb-apply-btn">{txf(c.will_change === 1 ? "Apply {0} change" : "Apply {0} changes", c.will_change)}</button>}
        {job.status === "validated" && <button className={btn} onClick={() => act("discard")} data-testid="cb-discard-btn">{tx("Discard")}</button>}
        {["applying", "rolling_back"].includes(job.status) && <button className={btn} onClick={() => act("pause")} data-testid="cb-pause-btn">{tx("Pause")}</button>}
        {["paused", "interrupted"].includes(job.status) && <button className={btn} onClick={() => act("resume")} data-testid="cb-resume-btn">{tx("Resume")}</button>}
        {["completed", "paused"].includes(job.status) && job.apply_started_at && !job.rollback_started_at && <button className={btn} onClick={rollback} data-testid="cb-rollback-btn">{tx("Roll back")}</button>}
      </div>
      <p className="text-[11px] text-[var(--mv-muted)]" data-testid="cb-job-counts">{OUTCOMES.map((o) => `${tx(o)} ${c[o] || 0}`).join(" · ")}{c.pending ? ` · ${tx("checking")} ${c.pending}` : ""}</p>
      <JobRows job={job} />
    </div>
  );
}

function CategoryAudit() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { setS((await api.get(`${B}/audit`)).data); } catch (e) { toast.error(tx(errMsg(e))); } finally { setBusy(false); } };
  const stat = (k, label) => <span data-testid={`cb-audit-${k}`}><b className="text-[var(--mv-text)]">{s[k]}</b> {tx(label)}</span>;
  return (
    <div className="rounded-2xl border border-[var(--mv-border)] p-4 text-xs text-[var(--mv-muted)]" data-testid="cb-audit">
      <p>{tx("Read-only category audit of every product against the current category tree. Nothing is changed.")}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button className={btn} disabled={busy} onClick={run} data-testid="cb-audit-run">{tx(busy ? "Checking…" : "Run category audit")}</button>
        <button className={btn} onClick={() => download(`${B}/audit.csv`, "mivo_category_audit_all.csv")} data-testid="cb-audit-all-csv">{tx("Download all products CSV")}</button>
        <button className={btn} onClick={() => download(`${B}/audit.csv?problems_only=true`, "mivo_category_problems.csv")} data-testid="cb-audit-problems-csv">{tx("Download problems CSV")}</button>
      </div>
      {s && <div className="mt-3 flex flex-wrap gap-4">
        {stat("total", "products")}{stat("valid", "valid")}{stat("missing", "missing")}{stat("invalid", "invalid")}{stat("needs_attention", "need attention")}
        {Object.entries(s.by_issue).map(([k, v]) => <span key={k} data-testid={`cb-audit-issue-${k}`}>{k}: {v}</span>)}
      </div>}
    </div>
  );
}

export function CategoryBulk() { useLang();
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const file = useRef();
  const load = async (pick) => {
    const { data } = await api.get(`${B}/jobs`);
    setJobs(data);
    const id = pick || open?.job_id || data[0]?.job_id;
    if (id) setOpen((await api.get(`${B}/jobs/${id}`)).data);
  };
  useEffect(() => { load().catch((e) => toast.error(tx(errMsg(e)))); }, []); // eslint-disable-line
  useEffect(() => { if (!ACTIVE.includes(open?.status)) return undefined; const t = setTimeout(() => load(), 2000); return () => clearTimeout(t); }, [open]); // eslint-disable-line
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const su = await uploadSheet(f);
      const { data } = await api.post(`${B}/jobs/from-upload`, { upload_id: su.upload_id });
      await load(data.job_id);
      toast.success(tx("Dry run started — nothing is changed until you apply"));
    } catch (e) { toast.error(tx(errMsg(e))); } finally { setBusy(false); file.current.value = ""; }
  };
  return (
    <div className="space-y-4" data-testid="category-bulk">
      <CategoryAudit />
      <div className="rounded-2xl border border-[var(--mv-border)] p-4 text-xs text-[var(--mv-muted)]">
        <p>{tx("Changes ONLY each product's category and subcategory. Prices, costs, stock, variants, photos, store, reviews and publish status are never touched.")}</p>
        <p className="mt-1">{tx("CSV columns: match_type (product_id, sku or item_number), match_value (exact), category_id, subcategory_id. Upload runs a dry run first.")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="cb-file-input" />
          <button className="mv-btn-primary rounded-lg px-3 py-1.5 text-xs disabled:opacity-40" disabled={busy} onClick={() => file.current.click()} data-testid="cb-upload-btn">{tx(busy ? "Uploading…" : "Upload CSV for dry run")}</button>
          <button className={btn} onClick={() => download(`${B}/template.csv`, "category_update_template.csv")} data-testid="cb-template-btn">{tx("Download template")}</button>
          {jobs.length > 1 && <select className="rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-2 py-1.5" value={open?.job_id || ""} onChange={async (e) => setOpen((await api.get(`${B}/jobs/${e.target.value}`)).data)} data-testid="cb-job-select">
            {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.file_name} · {j.status}</option>)}</select>}
        </div>
      </div>
      {open ? <JobCard job={open} reload={load} /> : <p className="text-sm text-[var(--mv-muted)]" data-testid="cb-empty">{tx("No category update jobs yet.")}</p>}
    </div>
  );
}
